import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Box,
  CheckCircle2,
  Clock,
  FileText,
  GitBranch,
  Loader2,
  PlayCircle,
  XCircle,
} from "lucide-react";
import { Link } from "react-router-dom";

import { CodeEditor } from "@/components/canonical/CodeEditor";
import { Alert } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  messagesApi,
  type MessageState,
  type TimelineEntry,
} from "@/lib/api-messages";

const STATE_TONE: Record<MessageState, "active" | "draft" | "deprecated" | "danger" | "warning" | "neutral"> = {
  RAW: "draft",
  CANONICAL: "neutral",
  ENRICHED: "neutral",
  COMPLETED: "active",
  PENDING_MAPPING: "warning",
  FAILED: "danger",
};

function outcomeIcon(outcome: TimelineEntry["outcome"]) {
  switch (outcome) {
    case "success":
      return <CheckCircle2 className="h-3.5 w-3.5 text-success" strokeWidth={1.5} />;
    case "failure":
      return <XCircle className="h-3.5 w-3.5 text-danger" strokeWidth={1.5} />;
    case "denied":
      return <XCircle className="h-3.5 w-3.5 text-warning" strokeWidth={1.5} />;
  }
}

export interface MessageDrawerProps {
  messageId: string | null;
  onOpenChange: (open: boolean) => void;
}

export function MessageDrawer({ messageId, onOpenChange }: MessageDrawerProps) {
  const qc = useQueryClient();
  const open = !!messageId;

  const detailQuery = useQuery({
    queryKey: ["messages", messageId],
    queryFn: () => messagesApi.get(messageId!),
    enabled: open,
  });

  const rawQuery = useQuery({
    queryKey: ["messages", messageId, "raw"],
    queryFn: () => messagesApi.fetchRaw(messageId!),
    enabled: open,
  });

  const replayMutation = useMutation({
    mutationFn: () => messagesApi.replay(messageId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["messages"] });
      qc.invalidateQueries({ queryKey: ["messages", messageId] });
    },
  });

  const detail = detailQuery.data;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            <SheetTitle className="text-sm font-semibold">
              Message{" "}
              <span className="font-mono text-xs text-muted-foreground">
                {messageId?.slice(0, 8)}…
              </span>
            </SheetTitle>
            {detail && (
              <Badge tone={STATE_TONE[detail.state]} className="ml-2">
                {detail.state}
              </Badge>
            )}
            {/* Trace pivot: every message is a valid anchor for the
                forward causal graph (executions + deliveries it
                triggered). Always rendered — trace is read-only. */}
            {messageId && (
              <Button
                asChild
                size="sm"
                variant="ghost"
                className="ml-auto h-7 gap-1.5 text-xs"
              >
                <Link to={`/trace/message/${messageId}`}>
                  <GitBranch className="h-3.5 w-3.5" strokeWidth={1.5} />
                  View trace
                </Link>
              </Button>
            )}
          </div>
          {detail && (
            <div className="text-xs text-muted-foreground space-y-0.5">
              <div>
                <span className="font-medium">source</span>: <span className="font-mono">{detail.source}</span>
                {" · "}
                <span className="font-medium">format</span>:{" "}
                <span className="font-mono">{detail.raw_format ?? "—"}</span>
                {detail.canonical_type && (
                  <>
                    {" · "}
                    <span className="font-medium">canonical</span>:{" "}
                    <span className="font-mono">{detail.canonical_type}</span>
                  </>
                )}
              </div>
              <div>
                <span className="font-medium">received</span>{" "}
                {new Date(detail.received_at).toLocaleString()}
                {detail.normalized_at && (
                  <>
                    {" · "}
                    <span className="font-medium">normalized</span>{" "}
                    {new Date(detail.normalized_at).toLocaleString()}
                  </>
                )}
              </div>
              <div className="font-mono text-xs">sha256: {detail.payload_hash}</div>
            </div>
          )}
        </SheetHeader>

        <SheetBody>
          {detailQuery.isLoading && (
            <div className="p-4 text-sm text-muted-foreground">Loading…</div>
          )}

          {detail && (
            <Tabs defaultValue="canonical" className="flex h-full flex-col overflow-hidden">
              <TabsList className="px-4 bg-surface">
                <TabsTrigger value="canonical">Canonical</TabsTrigger>
                <TabsTrigger value="raw">Raw</TabsTrigger>
                <TabsTrigger value="timeline">
                  Timeline ({detail.timeline.length})
                </TabsTrigger>
                <TabsTrigger value="deliveries">
                  Deliveries ({detail.deliveries.length})
                </TabsTrigger>
              </TabsList>

              <TabsContent value="canonical" className="flex-1 overflow-hidden">
                {detail.last_error && (
                  <div className="px-4 pt-3">
                    <Alert tone="danger" title="Normalize failed">
                      <code className="font-mono text-xs">{detail.last_error}</code>
                    </Alert>
                  </div>
                )}
                {detail.canonical_payload ? (
                  <div className="h-full p-3">
                    <CodeEditor
                      value={JSON.stringify(detail.canonical_payload, null, 2)}
                      language="json"
                      readOnly
                    />
                  </div>
                ) : (
                  <div className="p-4 text-sm text-muted-foreground">
                    No canonical payload yet (state: {detail.state}).
                  </div>
                )}
              </TabsContent>

              <TabsContent value="raw" className="flex-1 overflow-hidden">
                <div className="border-b border-border px-4 h-9 flex items-center justify-between text-xs text-muted-foreground bg-surface">
                  <div>
                    <span className="font-mono">{detail.raw_blob_ref}</span>
                  </div>
                  <a
                    href={`/api/admin/messages/${detail.message_id}/raw`}
                    download
                    className="hover:text-foreground underline"
                  >
                    Download
                  </a>
                </div>
                <div className="h-[calc(100%-2.25rem)] p-3">
                  {rawQuery.isLoading ? (
                    <div className="text-sm text-muted-foreground">Loading raw payload…</div>
                  ) : rawQuery.data ? (
                    <CodeEditor
                      value={rawQuery.data}
                      language={(detail.raw_format as "json" | "xml" | undefined) === "json" ? "json" : "json"}
                      readOnly
                    />
                  ) : (
                    <div className="text-sm text-danger">Failed to load raw payload.</div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="timeline" className="flex-1 overflow-auto">
                <div className="p-4">
                  {detail.timeline.length === 0 ? (
                    <div className="text-sm text-muted-foreground">
                      No timeline events recorded yet.
                    </div>
                  ) : (
                    <ol className="relative border-l border-border ml-3 space-y-3">
                      {detail.timeline.map((t, i) => (
                        <li key={i} className="ml-4">
                          <span className="absolute -left-[7px] mt-1 grid h-3 w-3 place-items-center bg-surface border border-border">
                            <Clock className="h-2 w-2 text-muted-foreground" strokeWidth={2} />
                          </span>
                          <div className="flex items-center gap-2">
                            {outcomeIcon(t.outcome)}
                            <span className="font-mono text-xs">{t.event_type}</span>
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {new Date(t.occurred_at).toLocaleString()}
                          </div>
                          {t.details && Object.keys(t.details).length > 0 && (
                            <details className="mt-1 cursor-pointer">
                              <summary className="text-xs text-muted-foreground hover:text-foreground">
                                details ({Object.keys(t.details).length})
                              </summary>
                              <pre className="mt-1 font-mono text-xs text-muted-foreground whitespace-pre-wrap">
                                {JSON.stringify(t.details, null, 2)}
                              </pre>
                            </details>
                          )}
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="deliveries" className="flex-1 overflow-auto">
                <div className="p-4 space-y-3">
                  {detail.deliveries.length === 0 ? (
                    <div className="flex flex-col items-center justify-center gap-2 text-sm text-muted-foreground py-12">
                      <Box className="h-6 w-6" strokeWidth={1.5} />
                      <span>No deliveries yet</span>
                      <span className="text-xs">
                        Add a routing rule that matches{" "}
                        <span className="font-mono">{detail.canonical_type}</span>.
                      </span>
                    </div>
                  ) : (
                    detail.deliveries.map((d) => (
                      <div key={d.delivery_id} className="border border-border">
                        <div className="flex items-center justify-between px-3 h-9 border-b border-border bg-surface">
                          <span className="font-mono text-xs">
                            {d.target_connector} → {d.target_id}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              attempts {d.attempts}
                            </span>
                            <Badge
                              tone={
                                d.state === "DELIVERED"
                                  ? "active"
                                  : d.state === "FAILED"
                                    ? "danger"
                                    : "neutral"
                              }
                            >
                              {d.state}
                            </Badge>
                          </div>
                        </div>
                        <div className="p-3 space-y-2">
                          <div className="text-xs text-muted-foreground space-y-0.5">
                            {d.delivered_at && (
                              <div>
                                delivered{" "}
                                <span className="font-mono">
                                  {new Date(d.delivered_at).toLocaleString()}
                                </span>
                              </div>
                            )}
                            {d.target_reference && (
                              <div>
                                target_reference{" "}
                                <span className="font-mono">{d.target_reference}</span>
                              </div>
                            )}
                            {d.target_blob_ref && (
                              <div>
                                blob <span className="font-mono">{d.target_blob_ref}</span>
                              </div>
                            )}
                          </div>
                          {d.last_error && (
                            <div className="text-xs text-danger font-mono">{d.last_error}</div>
                          )}
                          {d.target_payload && (
                            <details className="cursor-pointer">
                              <summary className="text-xs uppercase tracking-wider text-muted-foreground hover:text-foreground">
                                target_payload
                              </summary>
                              <div className="mt-2 h-48 border border-border">
                                <CodeEditor
                                  value={JSON.stringify(d.target_payload, null, 2)}
                                  language="json"
                                  readOnly
                                />
                              </div>
                            </details>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>
            </Tabs>
          )}
        </SheetBody>

        <SheetFooter>
          {replayMutation.error && (
            <span className="text-xs text-danger mr-auto">
              Replay failed: {(replayMutation.error as Error).message}
            </span>
          )}
          {replayMutation.isSuccess && (
            <span className="text-xs text-success mr-auto flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" strokeWidth={1.5} /> Replay queued
            </span>
          )}
          {detail && (detail.state === "FAILED" || detail.state === "CANONICAL") && (
            <Button
              size="sm"
              onClick={() => replayMutation.mutate()}
              disabled={replayMutation.isPending}
            >
              {replayMutation.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />
              ) : (
                <PlayCircle className="h-3.5 w-3.5" strokeWidth={1.5} />
              )}
              Replay normalize
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
